import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { usePermissions } from '@/hooks/usePermissions';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Eye, Trash2, Search } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface Produto {
  id: string;
  codigo_produto: string;
  nome_produto: string;
  categoria: string;
  valor: number;
  quantidade_minima: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  custo_produto?: number;
  valor_produto?: number;
  codigo_barra?: string;
  unidade_medida?: string;
  categoria_n1_id?: string;
  categoria_n2_id?: string;
  categoria_n3_id?: string;
  fornecedor_id?: string;
  empresa_id?: string;
  tempo_compra?: number;
  peso?: number;
  volume?: number;
  fee?: number;
  minimo_fee?: number;
  maximo_fee?: number;
  minimo_custo?: number;
  maximo_custo?: number;
}

interface ProdutoFormData {
  nome_produto: string;
  categoria: string;
  valor: number;
  quantidade_minima: number;
  custo_produto?: number;
  valor_produto?: number;
  codigo_barra?: string;
  unidade_medida?: string;
  categoria_n1_id?: string;
  categoria_n2_id?: string;
  categoria_n3_id?: string;
  fornecedor_id?: string;
  empresa_id?: string;
  tempo_compra?: number;
  peso?: number;
  volume?: number;
  fee?: number;
  minimo_fee?: number;
  maximo_fee?: number;
  minimo_custo?: number;
  maximo_custo?: number;
}

interface Categoria {
  id: string;
  nome: string;
}

interface Fornecedor {
  id: string;
  razao_social: string;
  apelido?: string;
}

interface Empresa {
  id: string;
  razao_social: string;
  apelido?: string;
}

export default function Produtos() {
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProduto, setSelectedProduto] = useState<Produto | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Categories state
  const [categoriasN1, setCategoriasN1] = useState<Categoria[]>([]);
  const [categoriasN2, setCategoriasN2] = useState<Categoria[]>([]);
  const [categoriasN3, setCategoriasN3] = useState<Categoria[]>([]);
  const [allCategoriasN2, setAllCategoriasN2] = useState<any[]>([]);
  const [allCategoriasN3, setAllCategoriasN3] = useState<any[]>([]);
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  
  const [formData, setFormData] = useState<ProdutoFormData>({
    nome_produto: '',
    categoria: 'Material de Escritório',
    valor: 0,
    quantidade_minima: 0,
    custo_produto: 0,
    valor_produto: 0,
    codigo_barra: '',
    unidade_medida: '',
    categoria_n1_id: 'none',
    categoria_n2_id: 'none',
    categoria_n3_id: 'none',
    fornecedor_id: 'none',
    empresa_id: 'none',
    tempo_compra: 0,
    peso: 0,
    volume: 0,
    fee: 0,
    minimo_fee: 0,
    maximo_fee: 0,
    minimo_custo: 0,
    maximo_custo: 0,
  });

  // Função para validar custos e fees
  const validateAndCalculate = (newFormData: ProdutoFormData) => {
    let errors: string[] = [];
    
    // Validação de custo dentro dos limites
    if (newFormData.custo_produto && newFormData.minimo_custo && newFormData.maximo_custo) {
      if (newFormData.custo_produto < newFormData.minimo_custo || newFormData.custo_produto > newFormData.maximo_custo) {
        errors.push(`Custo deve estar entre ${newFormData.minimo_custo} e ${newFormData.maximo_custo}`);
      }
    }
    
    // Validação de Fee dentro dos limites
    if (newFormData.fee && newFormData.minimo_fee && newFormData.maximo_fee) {
      if (newFormData.fee < newFormData.minimo_fee || newFormData.fee > newFormData.maximo_fee) {
        errors.push(`Fee deve estar entre ${newFormData.minimo_fee}% e ${newFormData.maximo_fee}%`);
      }
    }
    
    if (errors.length > 0) {
      toast({
        title: "Validação",
        description: errors.join('. '),
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  };

  // Função para atualizar form data com validações
  const updateFormData = (updates: Partial<ProdutoFormData>) => {
    const newFormData = { ...formData, ...updates };
    
    // Se mudou custo ou fee, recalcular
    if ('custo_produto' in updates || 'fee' in updates) {
      if (newFormData.custo_produto && newFormData.fee) {
        const novoValor = newFormData.custo_produto + (newFormData.custo_produto * (newFormData.fee / 100));
        newFormData.valor_produto = parseFloat(novoValor.toFixed(2));
        newFormData.valor = parseFloat(novoValor.toFixed(2));
      }
    }
    
    setFormData(newFormData);
  };
  
  const { toast } = useToast();
  const { hasPermission, loading: permissionsLoading } = usePermissions();

  useEffect(() => {
    loadProdutos();
    loadCategorias();
    loadFornecedores();
    loadEmpresas();
  }, []);

  const loadCategorias = async () => {
    try {
      // Load all categories
      const [n1Response, n2Response, n3Response] = await Promise.all([
        supabase.from('categorias_n1').select('id, nome').order('nome'),
        supabase.from('categorias_n2').select('id, nome, id_categoria_n1').order('nome'),
        supabase.from('categorias_n3').select('id, nome, id_categoria_n2').order('nome')
      ]);

      if (n1Response.error) throw n1Response.error;
      if (n2Response.error) throw n2Response.error;
      if (n3Response.error) throw n3Response.error;

      setCategoriasN1(n1Response.data || []);
      setAllCategoriasN2(n2Response.data || []);
      setAllCategoriasN3(n3Response.data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar categorias",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const loadFornecedores = async () => {
    try {
      // @ts-ignore - Temporary fix for deep type instantiation
      const { data, error } = await supabase
        .from('fornecedores')
        .select('id, razao_social, apelido')
        .eq('ativo', true)
        .order('razao_social');

      if (error) throw error;
      setFornecedores(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar fornecedores",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const loadEmpresas = async () => {
    try {
      // @ts-ignore - TypeScript type inference issue with Supabase
      const result: any = await supabase
        .from('empresas')
        .select('id, razao_social, apelido')
        .eq('ativo', true)
        .order('razao_social');
      
      const { data, error } = result;

      if (error) throw error;
      setEmpresas(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar empresas",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Update N2 categories when N1 changes
  useEffect(() => {
    if (formData.categoria_n1_id && formData.categoria_n1_id !== 'none') {
      const filteredN2 = allCategoriasN2.filter(cat => cat.id_categoria_n1 === formData.categoria_n1_id);
      setCategoriasN2(filteredN2);
      // Reset N2 and N3 selections
      setFormData(prev => ({ ...prev, categoria_n2_id: 'none', categoria_n3_id: 'none' }));
      setCategoriasN3([]);
    } else {
      setCategoriasN2([]);
      setCategoriasN3([]);
    }
  }, [formData.categoria_n1_id, allCategoriasN2]);

  // Update N3 categories when N2 changes
  useEffect(() => {
    if (formData.categoria_n2_id && formData.categoria_n2_id !== 'none') {
      const filteredN3 = allCategoriasN3.filter(cat => cat.id_categoria_n2 === formData.categoria_n2_id);
      setCategoriasN3(filteredN3);
      // Reset N3 selection
      setFormData(prev => ({ ...prev, categoria_n3_id: 'none' }));
    } else {
      setCategoriasN3([]);
    }
  }, [formData.categoria_n2_id, allCategoriasN3]);

  // Permission checks
  const canReadProdutos = !permissionsLoading && hasPermission('produtos_insumos', 'produtos_insumos', 'read');
  const canWriteProdutos = !permissionsLoading && hasPermission('produtos_insumos', 'produtos_insumos', 'write');
  const canAdminProdutos = !permissionsLoading && hasPermission('produtos_insumos', 'produtos_insumos', 'admin');

  const loadProdutos = async () => {
    try {
      // @ts-ignore - Temporary fix for missing properties
      const { data, error } = await supabase
        .from('produtos_insumos')
        .select('*')
        .order('nome_produto');

      if (error) throw error;
      // @ts-ignore - Temporary fix for missing ativo property
      setProdutos(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar produtos",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar antes de enviar
    if (!validateAndCalculate(formData)) {
      return;
    }
    
    try {
      // Prepare data and convert 'none' values to null
      const dataToInsert = {
        ...formData,
        categoria_n1_id: formData.categoria_n1_id === 'none' ? null : formData.categoria_n1_id,
        categoria_n2_id: formData.categoria_n2_id === 'none' ? null : formData.categoria_n2_id,
        categoria_n3_id: formData.categoria_n3_id === 'none' ? null : formData.categoria_n3_id,
        fornecedor_id: formData.fornecedor_id === 'none' ? null : formData.fornecedor_id,
        empresa_id: formData.empresa_id === 'none' ? null : formData.empresa_id,
      };
      
      const { error } = await supabase
        .from('produtos_insumos')
        .insert({
          ...dataToInsert,
          categoria: 'Material de Escritório', // Valor padrão válido para categoria
          ativo: true,
        });

      if (error) throw error;

      toast({
        title: "Produto criado",
        description: "Produto adicionado com sucesso.",
      });

      setIsModalOpen(false);
      resetForm();
      loadProdutos();
    } catch (error: any) {
      toast({
        title: "Erro ao criar produto",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleView = (produto: Produto) => {
    setSelectedProduto(produto);
    setIsViewModalOpen(true);
  };

  const handleEdit = (produto: Produto) => {
    setSelectedProduto(produto);
    // Preencher o formData com dados do produto selecionado
    setFormData({
      nome_produto: produto.nome_produto,
      categoria: produto.categoria,
      valor: produto.valor,
      quantidade_minima: produto.quantidade_minima,
      custo_produto: produto.custo_produto || 0,
      valor_produto: produto.valor_produto || 0,
      codigo_barra: produto.codigo_barra || '',
      unidade_medida: produto.unidade_medida || '',
      categoria_n1_id: produto.categoria_n1_id || 'none',
      categoria_n2_id: produto.categoria_n2_id || 'none',
      categoria_n3_id: produto.categoria_n3_id || 'none',
      fornecedor_id: produto.fornecedor_id || 'none',
      empresa_id: produto.empresa_id || 'none',
      tempo_compra: produto.tempo_compra || 0,
      peso: produto.peso || 0,
      volume: produto.volume || 0,
      fee: produto.fee || 0,
      minimo_fee: produto.minimo_fee || 0,
      maximo_fee: produto.maximo_fee || 0,
      minimo_custo: produto.minimo_custo || 0,
      maximo_custo: produto.maximo_custo || 0,
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = async (produto: Produto) => {
    if (window.confirm(`Tem certeza que deseja excluir o produto "${produto.nome_produto}"?`)) {
      try {
        const { error } = await supabase
          .from('produtos_insumos')
          .delete()
          .eq('id', produto.id);

        if (error) throw error;

        toast({
          title: "Produto excluído",
          description: "Produto removido com sucesso.",
        });

        loadProdutos();
      } catch (error: any) {
        toast({
          title: "Erro ao excluir produto",
          description: error.message,
          variant: "destructive",
        });
      }
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProduto) return;
    
    // Validar antes de enviar
    if (!validateAndCalculate(formData)) {
      return;
    }
    
    try {
      // Prepare data and convert 'none' values to null
      const dataToUpdate = {
        ...formData,
        categoria_n1_id: formData.categoria_n1_id === 'none' ? null : formData.categoria_n1_id,
        categoria_n2_id: formData.categoria_n2_id === 'none' ? null : formData.categoria_n2_id,
        categoria_n3_id: formData.categoria_n3_id === 'none' ? null : formData.categoria_n3_id,
        fornecedor_id: formData.fornecedor_id === 'none' ? null : formData.fornecedor_id,
        empresa_id: formData.empresa_id === 'none' ? null : formData.empresa_id,
      };
      
      const { error } = await supabase
        .from('produtos_insumos')
        .update(dataToUpdate)
        .eq('id', selectedProduto.id);

      if (error) throw error;

      toast({
        title: "Produto atualizado",
        description: "Produto atualizado com sucesso.",
      });

      setIsEditModalOpen(false);
      setSelectedProduto(null);
      resetForm();
      loadProdutos();
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar produto",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      nome_produto: '',
      categoria: 'Material de Escritório',
      valor: 0,
      quantidade_minima: 0,
      custo_produto: 0,
      valor_produto: 0,
      codigo_barra: '',
      unidade_medida: '',
      categoria_n1_id: 'none',
      categoria_n2_id: 'none',
      categoria_n3_id: 'none',
      fornecedor_id: 'none',
      empresa_id: 'none',
      tempo_compra: 0,
      peso: 0,
      volume: 0,
      fee: 0,
      minimo_fee: 0,
      maximo_fee: 0,
      minimo_custo: 0,
      maximo_custo: 0,
    });
  };

  // Filter products based on search and status
  const filteredProdutos = produtos.filter(produto => {
    const matchesSearch = produto.nome_produto.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         produto.codigo_produto.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         produto.categoria.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && produto.ativo) ||
                         (statusFilter === 'inactive' && !produto.ativo);
    
    return matchesSearch && matchesStatus;
  });

  if (loading || permissionsLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Carregando produtos...</div>
      </div>
    );
  }

  if (!canReadProdutos) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-muted-foreground">Acesso Negado</h2>
          <p className="text-muted-foreground">Você não tem permissão para visualizar produtos.</p>
        </div>
      </div>
    );
  }

  return (
    <ProtectedRoute>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Produtos</h1>
            <p className="text-muted-foreground">Gerencie os produtos e insumos</p>
          </div>
          {canWriteProdutos && (
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Novo Produto
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Adicionar Novo Produto</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nome_produto">Nome do Produto *</Label>
                      <Input
                        id="nome_produto"
                        value={formData.nome_produto}
                        onChange={(e) => setFormData({ ...formData, nome_produto: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="categoria">Categoria *</Label>
                      <Select
                        value={formData.categoria}
                        onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione uma categoria..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Alimentos e Bebidas">Alimentos e Bebidas</SelectItem>
                          <SelectItem value="Produtos de Limpeza">Produtos de Limpeza</SelectItem>
                          <SelectItem value="Utensílios de Copa e Cozinha">Utensílios de Copa e Cozinha</SelectItem>
                          <SelectItem value="Eletrodomésticos e Eletrônicos">Eletrodomésticos e Eletrônicos</SelectItem>
                          <SelectItem value="Material de Escritório">Material de Escritório</SelectItem>
                          <SelectItem value="Móveis e Decoração">Móveis e Decoração</SelectItem>
                          <SelectItem value="Livros">Livros</SelectItem>
                          <SelectItem value="Equipamentos de Proteção">Equipamentos de Proteção</SelectItem>
                          <SelectItem value="Ferramentas e Acessórios">Ferramentas e Acessórios</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Categorias Hierárquicas */}
                  <div className="space-y-4">
                    <Label className="text-lg font-semibold">Categorias Hierárquicas (Opcionais)</Label>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="categoria_n1">Categoria Nível 1</Label>
                        <Select
                          value={formData.categoria_n1_id}
                          onValueChange={(value) => setFormData({ ...formData, categoria_n1_id: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nenhuma</SelectItem>
                            {categoriasN1.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="categoria_n2">Categoria Nível 2</Label>
                        <Select
                          value={formData.categoria_n2_id}
                          onValueChange={(value) => setFormData({ ...formData, categoria_n2_id: value })}
                          disabled={!formData.categoria_n1_id || formData.categoria_n1_id === 'none'}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione N1 primeiro..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nenhuma</SelectItem>
                            {categoriasN2.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="categoria_n3">Categoria Nível 3</Label>
                        <Select
                          value={formData.categoria_n3_id}
                          onValueChange={(value) => setFormData({ ...formData, categoria_n3_id: value })}
                          disabled={!formData.categoria_n2_id || formData.categoria_n2_id === 'none'}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione N2 primeiro..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nenhuma</SelectItem>
                            {categoriasN3.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="unidade_medida">Unidade de Medida</Label>
                      <Input
                        id="unidade_medida"
                        value={formData.unidade_medida}
                        onChange={(e) => setFormData({ ...formData, unidade_medida: e.target.value })}
                        placeholder="ex: UN, KG, M, L"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="codigo_barra">Código de Barras</Label>
                      <Input
                        id="codigo_barra"
                        value={formData.codigo_barra}
                        onChange={(e) => setFormData({ ...formData, codigo_barra: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="fornecedor">Fornecedor</Label>
                      <Select
                        value={formData.fornecedor_id}
                        onValueChange={(value) => setFormData({ ...formData, fornecedor_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um fornecedor..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nenhum</SelectItem>
                          {fornecedores.map((fornecedor) => (
                            <SelectItem key={fornecedor.id} value={fornecedor.id}>
                              {fornecedor.apelido || fornecedor.razao_social}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="empresa">Empresa</Label>
                    <Select
                      value={formData.empresa_id}
                      onValueChange={(value) => setFormData({ ...formData, empresa_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma empresa..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhuma</SelectItem>
                        {empresas.map((empresa) => (
                          <SelectItem key={empresa.id} value={empresa.id}>
                            {empresa.apelido || empresa.razao_social}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tempo_compra">Tempo de Compra (dias)</Label>
                      <Input
                        id="tempo_compra"
                        type="number"
                        value={formData.tempo_compra}
                        onChange={(e) => setFormData({ ...formData, tempo_compra: Number(e.target.value) })}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="peso">Peso (kg)</Label>
                      <Input
                        id="peso"
                        type="number"
                        step="0.001"
                        value={formData.peso}
                        onChange={(e) => setFormData({ ...formData, peso: Number(e.target.value) })}
                        placeholder="0.000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="volume">Volume (m³)</Label>
                      <Input
                        id="volume"
                        type="number"
                        step="0.001"
                        value={formData.volume}
                        onChange={(e) => setFormData({ ...formData, volume: Number(e.target.value) })}
                        placeholder="0.000"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="valor">Valor de Venda *</Label>
                  <Input
                    id="valor"
                    type="number"
                    step="0.01"
                    value={formData.valor}
                    onChange={(e) => updateFormData({ valor: Number(e.target.value), valor_produto: Number(e.target.value) })}
                    required
                  />
                </div>
                    <div className="space-y-2">
                      <Label htmlFor="custo_produto">Custo do Produto</Label>
                      <Input
                        id="custo_produto"
                        type="number"
                        step="0.01"
                        value={formData.custo_produto}
                        onChange={(e) => updateFormData({ custo_produto: Number(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quantidade_minima">Quantidade Mínima *</Label>
                      <Input
                        id="quantidade_minima"
                        type="number"
                        value={formData.quantidade_minima}
                        onChange={(e) => setFormData({ ...formData, quantidade_minima: Number(e.target.value) })}
                        required
                      />
                    </div>
                  </div>

                  {/* Seção de Fee e Margens */}
                  <div className="space-y-4">
                    <Label className="text-lg font-semibold">Margem e Fee (Opcionais)</Label>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="fee">Fee (Margem) %</Label>
                        <Input
                          id="fee"
                          type="number"
                          step="0.01"
                          value={formData.fee}
                          onChange={(e) => updateFormData({ fee: Number(e.target.value) })}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="minimo_fee">Mínimo de Fee %</Label>
                        <Input
                          id="minimo_fee"
                          type="number"
                          step="0.01"
                          value={formData.minimo_fee}
                          onChange={(e) => setFormData({ ...formData, minimo_fee: Number(e.target.value) })}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="maximo_fee">Máximo de Fee %</Label>
                        <Input
                          id="maximo_fee"
                          type="number"
                          step="0.01"
                          value={formData.maximo_fee}
                          onChange={(e) => setFormData({ ...formData, maximo_fee: Number(e.target.value) })}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="minimo_custo">Mínimo de Custo</Label>
                        <Input
                          id="minimo_custo"
                          type="number"
                          step="0.01"
                          value={formData.minimo_custo}
                          onChange={(e) => setFormData({ ...formData, minimo_custo: Number(e.target.value) })}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="maximo_custo">Máximo de Custo</Label>
                        <Input
                          id="maximo_custo"
                          type="number"
                          step="0.01"
                          value={formData.maximo_custo}
                          onChange={(e) => setFormData({ ...formData, maximo_custo: Number(e.target.value) })}
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    {formData.fee && formData.custo_produto && formData.valor_produto && (
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">
                          <strong>Fee calculado automaticamente:</strong> {formData.fee.toFixed(2)}%
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Fórmula: (Valor do Produto – Custo) / Custo = ({formData.valor_produto} - {formData.custo_produto}) / {formData.custo_produto}
                        </p>
                      </div>
                    )}

                    {formData.valor_produto && formData.valor_produto !== formData.valor && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-700">
                          <strong>Valor calculado automaticamente:</strong> R$ {formData.valor_produto.toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit">
                      Criar Produto
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar produtos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Ativos</SelectItem>
              <SelectItem value="inactive">Inativos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Lista de Produtos</CardTitle>
            <CardDescription>
              {filteredProdutos.length} produto(s) encontrado(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Qtd. Mínima</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Data Criação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProdutos.map((produto) => (
                  <TableRow key={produto.id}>
                    <TableCell className="font-mono">{produto.codigo_produto}</TableCell>
                    <TableCell className="font-medium">{produto.nome_produto}</TableCell>
                    <TableCell>{produto.categoria || '-'}</TableCell>
                    <TableCell>R$ {Number(produto.valor || 0).toFixed(2)}</TableCell>
                    <TableCell>{produto.quantidade_minima}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        produto.ativo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {produto.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {new Date(produto.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => handleView(produto)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canWriteProdutos && (
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(produto)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {canAdminProdutos && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(produto)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Modal de Visualização */}
        <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Visualizar Produto</DialogTitle>
            </DialogHeader>
            {selectedProduto && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="font-semibold">Nome do Produto</Label>
                  <div className="p-3 bg-muted rounded-md">{selectedProduto.nome_produto}</div>
                </div>

                {/* Categorias Hierárquicas */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold">Categorias Hierárquicas</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Categoria Nível 1</Label>
                      <div className="p-3 bg-muted rounded-md">
                        {categoriasN1.find(cat => cat.id === selectedProduto.categoria_n1_id)?.nome || 'Nenhuma'}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Categoria Nível 2</Label>
                      <div className="p-3 bg-muted rounded-md">
                        {allCategoriasN2.find(cat => cat.id === selectedProduto.categoria_n2_id)?.nome || 'Nenhuma'}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Categoria Nível 3</Label>
                      <div className="p-3 bg-muted rounded-md">
                        {allCategoriasN3.find(cat => cat.id === selectedProduto.categoria_n3_id)?.nome || 'Nenhuma'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Unidade de Medida</Label>
                    <div className="p-3 bg-muted rounded-md">{selectedProduto.unidade_medida || '-'}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Código de Barras</Label>
                    <div className="p-3 bg-muted rounded-md">{selectedProduto.codigo_barra || '-'}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Fornecedor</Label>
                    <div className="p-3 bg-muted rounded-md">
                      {fornecedores.find(f => f.id === selectedProduto.fornecedor_id)?.razao_social || 'Nenhum'}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Empresa</Label>
                  <div className="p-3 bg-muted rounded-md">
                    {empresas.find(e => e.id === selectedProduto.empresa_id)?.razao_social || 'Nenhuma'}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tempo de Compra (dias)</Label>
                    <div className="p-3 bg-muted rounded-md">{selectedProduto.tempo_compra || 0}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Peso (kg)</Label>
                    <div className="p-3 bg-muted rounded-md">{selectedProduto.peso || 0}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Volume (m³)</Label>
                    <div className="p-3 bg-muted rounded-md">{selectedProduto.volume || 0}</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Valor de Venda</Label>
                    <div className="p-3 bg-muted rounded-md">R$ {Number(selectedProduto.valor || 0).toFixed(2)}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Custo do Produto</Label>
                    <div className="p-3 bg-muted rounded-md">R$ {Number(selectedProduto.custo_produto || 0).toFixed(2)}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Quantidade Mínima</Label>
                    <div className="p-3 bg-muted rounded-md">{selectedProduto.quantidade_minima}</div>
                  </div>
                </div>

                {/* Seção de Fee e Margens */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold">Margem e Fee</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Fee (Margem) %</Label>
                      <div className="p-3 bg-muted rounded-md">{selectedProduto.fee || 0}%</div>
                    </div>
                    <div className="space-y-2">
                      <Label>Mínimo de Fee %</Label>
                      <div className="p-3 bg-muted rounded-md">{selectedProduto.minimo_fee || 0}%</div>
                    </div>
                    <div className="space-y-2">
                      <Label>Máximo de Fee %</Label>
                      <div className="p-3 bg-muted rounded-md">{selectedProduto.maximo_fee || 0}%</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Mínimo de Custo</Label>
                      <div className="p-3 bg-muted rounded-md">R$ {Number(selectedProduto.minimo_custo || 0).toFixed(2)}</div>
                    </div>
                    <div className="space-y-2">
                      <Label>Máximo de Custo</Label>
                      <div className="p-3 bg-muted rounded-md">R$ {Number(selectedProduto.maximo_custo || 0).toFixed(2)}</div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Código</Label>
                    <div className="p-3 bg-muted rounded-md font-mono">{selectedProduto.codigo_produto}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <div className="p-3 bg-muted rounded-md">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        selectedProduto.ativo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedProduto.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <div className="p-3 bg-muted rounded-md">{selectedProduto.categoria || '-'}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data de Criação</Label>
                    <div className="p-3 bg-muted rounded-md">{new Date(selectedProduto.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Última Atualização</Label>
                    <div className="p-3 bg-muted rounded-md">{new Date(selectedProduto.updated_at).toLocaleDateString()}</div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Edição */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Editar Produto</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome_produto_edit">Nome do Produto *</Label>
                  <Input
                    id="nome_produto_edit"
                    value={formData.nome_produto}
                    onChange={(e) => setFormData({ ...formData, nome_produto: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="categoria_edit">Categoria *</Label>
                  <Select
                    value={formData.categoria}
                    onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma categoria..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Alimentos e Bebidas">Alimentos e Bebidas</SelectItem>
                      <SelectItem value="Produtos de Limpeza">Produtos de Limpeza</SelectItem>
                      <SelectItem value="Utensílios de Copa e Cozinha">Utensílios de Copa e Cozinha</SelectItem>
                      <SelectItem value="Eletrodomésticos e Eletrônicos">Eletrodomésticos e Eletrônicos</SelectItem>
                      <SelectItem value="Material de Escritório">Material de Escritório</SelectItem>
                      <SelectItem value="Móveis e Decoração">Móveis e Decoração</SelectItem>
                      <SelectItem value="Livros">Livros</SelectItem>
                      <SelectItem value="Equipamentos de Proteção">Equipamentos de Proteção</SelectItem>
                      <SelectItem value="Ferramentas e Acessórios">Ferramentas e Acessórios</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Categorias Hierárquicas */}
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Categorias Hierárquicas (Opcionais)</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="categoria_n1_edit">Categoria Nível 1</Label>
                    <Select
                      value={formData.categoria_n1_id}
                      onValueChange={(value) => setFormData({ ...formData, categoria_n1_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhuma</SelectItem>
                        {categoriasN1.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="categoria_n2_edit">Categoria Nível 2</Label>
                    <Select
                      value={formData.categoria_n2_id}
                      onValueChange={(value) => setFormData({ ...formData, categoria_n2_id: value })}
                      disabled={!formData.categoria_n1_id || formData.categoria_n1_id === 'none'}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione N1 primeiro..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhuma</SelectItem>
                        {categoriasN2.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="categoria_n3_edit">Categoria Nível 3</Label>
                    <Select
                      value={formData.categoria_n3_id}
                      onValueChange={(value) => setFormData({ ...formData, categoria_n3_id: value })}
                      disabled={!formData.categoria_n2_id || formData.categoria_n2_id === 'none'}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione N2 primeiro..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhuma</SelectItem>
                        {categoriasN3.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="unidade_medida_edit">Unidade de Medida</Label>
                  <Input
                    id="unidade_medida_edit"
                    value={formData.unidade_medida}
                    onChange={(e) => setFormData({ ...formData, unidade_medida: e.target.value })}
                    placeholder="ex: UN, KG, M, L"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="codigo_barra_edit">Código de Barras</Label>
                  <Input
                    id="codigo_barra_edit"
                    value={formData.codigo_barra}
                    onChange={(e) => setFormData({ ...formData, codigo_barra: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fornecedor_edit">Fornecedor</Label>
                  <Select
                    value={formData.fornecedor_id}
                    onValueChange={(value) => setFormData({ ...formData, fornecedor_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um fornecedor..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {fornecedores.map((fornecedor) => (
                        <SelectItem key={fornecedor.id} value={fornecedor.id}>
                          {fornecedor.apelido || fornecedor.razao_social}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="empresa_edit">Empresa</Label>
                <Select
                  value={formData.empresa_id}
                  onValueChange={(value) => setFormData({ ...formData, empresa_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma empresa..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {empresas.map((empresa) => (
                      <SelectItem key={empresa.id} value={empresa.id}>
                        {empresa.apelido || empresa.razao_social}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tempo_compra_edit">Tempo de Compra (dias)</Label>
                  <Input
                    id="tempo_compra_edit"
                    type="number"
                    value={formData.tempo_compra}
                    onChange={(e) => setFormData({ ...formData, tempo_compra: Number(e.target.value) })}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="peso_edit">Peso (kg)</Label>
                  <Input
                    id="peso_edit"
                    type="number"
                    step="0.001"
                    value={formData.peso}
                    onChange={(e) => setFormData({ ...formData, peso: Number(e.target.value) })}
                    placeholder="0.000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="volume_edit">Volume (m³)</Label>
                  <Input
                    id="volume_edit"
                    type="number"
                    step="0.001"
                    value={formData.volume}
                    onChange={(e) => setFormData({ ...formData, volume: Number(e.target.value) })}
                    placeholder="0.000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="valor_edit">Valor de Venda *</Label>
                  <Input
                    id="valor_edit"
                    type="number"
                    step="0.01"
                    value={formData.valor}
                    onChange={(e) => updateFormData({ valor: Number(e.target.value), valor_produto: Number(e.target.value) })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="custo_produto_edit">Custo do Produto</Label>
                  <Input
                    id="custo_produto_edit"
                    type="number"
                    step="0.01"
                    value={formData.custo_produto}
                    onChange={(e) => updateFormData({ custo_produto: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quantidade_minima_edit">Quantidade Mínima *</Label>
                  <Input
                    id="quantidade_minima_edit"
                    type="number"
                    value={formData.quantidade_minima}
                    onChange={(e) => setFormData({ ...formData, quantidade_minima: Number(e.target.value) })}
                    required
                  />
                </div>
              </div>

              {/* Seção de Fee e Margens */}
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Margem e Fee (Opcionais)</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fee_edit">Fee (Margem) %</Label>
                    <Input
                      id="fee_edit"
                      type="number"
                      step="0.01"
                      value={formData.fee}
                      onChange={(e) => updateFormData({ fee: Number(e.target.value) })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="minimo_fee_edit">Mínimo de Fee %</Label>
                    <Input
                      id="minimo_fee_edit"
                      type="number"
                      step="0.01"
                      value={formData.minimo_fee}
                      onChange={(e) => setFormData({ ...formData, minimo_fee: Number(e.target.value) })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maximo_fee_edit">Máximo de Fee %</Label>
                    <Input
                      id="maximo_fee_edit"
                      type="number"
                      step="0.01"
                      value={formData.maximo_fee}
                      onChange={(e) => setFormData({ ...formData, maximo_fee: Number(e.target.value) })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="minimo_custo_edit">Mínimo de Custo</Label>
                    <Input
                      id="minimo_custo_edit"
                      type="number"
                      step="0.01"
                      value={formData.minimo_custo}
                      onChange={(e) => setFormData({ ...formData, minimo_custo: Number(e.target.value) })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maximo_custo_edit">Máximo de Custo</Label>
                    <Input
                      id="maximo_custo_edit"
                      type="number"
                      step="0.01"
                      value={formData.maximo_custo}
                      onChange={(e) => setFormData({ ...formData, maximo_custo: Number(e.target.value) })}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {formData.fee && formData.custo_produto && formData.valor_produto && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Fee calculado automaticamente:</strong> {formData.fee.toFixed(2)}%
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Fórmula: (Valor do Produto – Custo) / Custo = ({formData.valor_produto} - {formData.custo_produto}) / {formData.custo_produto}
                    </p>
                  </div>
                )}

                {formData.valor_produto && formData.valor_produto !== formData.valor && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>Valor calculado automaticamente:</strong> R$ {formData.valor_produto.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsEditModalOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  Atualizar Produto
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </ProtectedRoute>
  );
}